import { Component, OnInit, signal, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AdminHeader } from '../../../services/admin-header';
import { ModalService } from '../../../services/modal.service';

interface Service {
	id: number;
	service_name: string;
	description: string;
	short_description: string;
	pricing: number;
	status: boolean;
	image_name: string;
	image_path: string;
	creator?: {
		id: number;
		first_name: string;
		last_name: string;
		email: string;
	};
}

@Component({
	selector: 'app-services',
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './services.html',
	styleUrl: './services.css',
})
export class Services implements OnInit, OnDestroy {
	services = signal<Service[]>([]);
	isLoading = signal(true);
	isEditMode = signal(false);
	isSubmitting = signal(false);
	errorMessage = signal('');
	selectedService = signal<Service | null>(null);
	imagePreview = signal<string | null>(null);
	selectedFile: File | null = null;

	@ViewChild('viewServiceModalTemplate') viewServiceModalTemplate!: TemplateRef<any>;

	serviceForm: FormGroup;

	constructor(
		private fb: FormBuilder,
		private http: HttpClient,
		private router: Router,
		private adminHeaderService: AdminHeader,
		private modalService: ModalService
	) {
		this.serviceForm = this.fb.group({
			service_name: ['', Validators.required],
			short_description: ['', Validators.required],
			description: ['', Validators.required],
			pricing: ['', [Validators.required, Validators.min(0)]],
			image: [null, Validators.required],
		});
	}

	ngOnInit() {
		this.loadServices();

		// Set header action button
		this.adminHeaderService.setHeaderAction({
			label: 'Add New Service',
			icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
			callback: () => this.openServiceModal()
		});
	}

	ngOnDestroy() {
		// Clear header action when component is destroyed
		this.adminHeaderService.clearHeaderAction();
		// Close modal if open when navigating away
		this.modalService.close();
	}

	loadServices() {
		this.isLoading.set(true);
		const apiUrl = `${environment.apiUrl}/services`;

		this.http.get<any>(apiUrl).subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.services.set(response.data);
				}
				this.isLoading.set(false);
			},
			error: (error) => {
				console.error('Error loading services:', error);
				this.isLoading.set(false);
			},
		});
	}

	openServiceModal() {
		this.router.navigate(['/admin/services/add']);
	}

	editService(service: Service) {
		this.router.navigate(['/admin/services/edit', service.id]);
	}

	viewService(service: Service) {
		this.selectedService.set(service);
		
		// Open modal using modal service
		this.modalService.open(this.viewServiceModalTemplate, '896px', () => {
			// onClose callback
			this.selectedService.set(null);
		});
	}

	closeModal() {
		this.serviceForm.reset();
		this.imagePreview.set(null);
		this.selectedFile = null;
		this.errorMessage.set('');
		this.modalService.close();
	}

	closeViewModal() {
		this.modalService.close();
	}

	onFileSelected(event: any) {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				this.errorMessage.set('Please select an image file');
				return;
			}

			// Validate file size (5MB)
			if (file.size > 5 * 1024 * 1024) {
				this.errorMessage.set('Image size should not exceed 5MB');
				return;
			}

			this.selectedFile = file;
			this.serviceForm.patchValue({ image: file });
			this.serviceForm.get('image')?.markAsTouched();

			// Create preview
			const reader = new FileReader();
			reader.onload = (e: any) => {
				this.imagePreview.set(e.target.result);
			};
			reader.readAsDataURL(file);
		}
	}

	removeImage() {
		this.imagePreview.set(null);
		this.selectedFile = null;
		this.serviceForm.patchValue({ image: null });

		// If creating new service, image is required
		if (!this.isEditMode()) {
			this.serviceForm.get('image')?.setValidators(Validators.required);
			this.serviceForm.get('image')?.updateValueAndValidity();
		}
	}

	submitForm() {
		// Mark all fields as touched to show validation errors
		Object.keys(this.serviceForm.controls).forEach((key) => {
			this.serviceForm.get(key)?.markAsTouched();
		});

		if (this.serviceForm.invalid) {
			return;
		}

		// For edit mode, if no new image is selected and we have an existing image, it's valid
		if (this.isEditMode() && !this.selectedFile && this.selectedService()?.image_path) {
			// Valid, proceed without image
		} else if (!this.selectedFile) {
			this.errorMessage.set('Please select an image');
			return;
		}

		this.isSubmitting.set(true);
		this.errorMessage.set('');

		const formData = new FormData();
		formData.append('service_name', this.serviceForm.value.service_name);
		formData.append('short_description', this.serviceForm.value.short_description);
		formData.append('description', this.serviceForm.value.description);
		formData.append('pricing', this.serviceForm.value.pricing);

		if (this.selectedFile) {
			formData.append('image', this.selectedFile);
		}

		const apiUrl = this.isEditMode()
			? `${environment.apiUrl}/services/${this.selectedService()?.id}`
			: `${environment.apiUrl}/services`;

		const request = this.isEditMode()
			? this.http.post<any>(apiUrl, formData)
			: this.http.post<any>(apiUrl, formData);

		request.subscribe({
			next: (response) => {
				this.isSubmitting.set(false);
				if (response.status) {
					this.closeModal();
					this.loadServices();
				} else {
					this.errorMessage.set(response.message || 'Operation failed');
				}
			},
			error: (error) => {
				console.error('Error:', error);
				this.isSubmitting.set(false);
				this.errorMessage.set(error.error?.message || 'An error occurred');
			},
		});
	}

	isFieldInvalid(fieldName: string): boolean {
		const field = this.serviceForm.get(fieldName);
		return !!(field && field.invalid && field.touched);
	}

	getImageUrl(path: string): string {
		if (path.startsWith('http')) {
			return path;
		}
		return `${environment.storageUrl}/${path}`;
	}

	onImageError(event: any) {
		event.target.src =
			'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VhZWFlNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
	}
}
